import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, Link, Text, toaster } from '@optiaxiom/react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Edge,
  Node,
  OnEdgesChange,
  OnNodesChange,
} from '@xyflow/react';

import { workflowAgentAtom } from '@frontend/atoms';
import { Sidebar } from '@frontend/components';
import { ConfirmationModal } from '@frontend/components/confirmationModal';

import { SpecializedAgentCreationForm } from '../../specialized-agent/agent-form/SpecializedAgentForm';
import { WorkflowErrorContext, WorkflowErrorContextValue } from '../contexts/WorkflowErrorContext';
import { useNodeValidation } from '../hooks/useNodeValidation';
import { useWorkflow } from '../hooks/useWorkflow';
import { useWorkflowCanvas } from '../hooks/useWorkflowCanvas';
import { useWorkflowDirtyState } from '../hooks/useWorkflowDirtyState';
import { WorkflowNode } from '../types/graph';

import WorkFlowLeftSidebar, { WorkflowRightSidebar } from './workflow-sidebar';
import { styles } from './WorkflowBuilder.css';
import { WorkflowBuilderTopbar } from './WorkflowBuilderTopbar';
import { WorkflowCanvas } from './WorkflowCanvas';

type WorkflowBuilderProps = {
  onClose: () => void;
  onSave: (draftNodes: Node[], draftEdges: Edge[]) => void;
  initialNodes: Node[];
  initialEdges: Edge[];
};

export function WorkflowBuilder({
  onClose,
  onSave,
  initialNodes,
  initialEdges,
}: WorkflowBuilderProps) {
  const { getNodeConfig } = useWorkflow();

  // Local draft state
  const [draftNodes, setDraftNodes] = useState<Node[]>(initialNodes);
  const [draftEdges, setDraftEdges] = useState<Edge[]>(initialEdges);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [forcedSidebarNode, setForcedSidebarNode] = useState<WorkflowNode | null>(null);
  const [hasUnsavedAsyncNode, setHasUnsavedAsyncNode] = useState(false);
  const [isSACreationOpen, setIsSACreationOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [agentFormData] = useAtom(workflowAgentAtom);

  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    const foundNode = draftNodes.find(node => node.id === selectedNodeId);
    return foundNode ? (foundNode as WorkflowNode) : null;
  }, [selectedNodeId, draftNodes]);

  useEffect(() => {
    setDraftNodes(initialNodes);
    setDraftEdges(initialEdges);
  }, [initialNodes, initialEdges]);

  // Use dirty state hook for unsaved changes detection
  const { showUnsavedChangesModal, handleClose, handleConfirmClose, handleCancelClose } =
    useWorkflowDirtyState({
      draftNodes,
      draftEdges,
      initialNodes,
      initialEdges,
      onClose,
    });

  const handleMenuClick = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const handleNodeClick = (_event: React.MouseEvent, node: Node) => {
    if (isSACreationOpen) {
      setIsSACreationOpen(false); // Close SA creation sidebar
    }
    setSelectedNodeId(node.id);
  };

  const handleOpenSACreation = () => {
    setIsSACreationOpen(true);
    setSelectedNodeId(null); // Close node config sidebar
    setForcedSidebarNode(null);
  };

  const handleCloseSACreation = () => {
    setIsSACreationOpen(false);
  };

  const handleNodeDropped = useCallback(() => {
    setSelectedNodeId(null);
    setForcedSidebarNode(null);
  }, [setSelectedNodeId, setForcedSidebarNode]);
  const handleAsyncNodeDropped = useCallback(
    (node: Node) => {
      setSelectedNodeId(node.id);
      setForcedSidebarNode(node as WorkflowNode);
      setHasUnsavedAsyncNode(true);
    },
    [setSelectedNodeId, setForcedSidebarNode, setHasUnsavedAsyncNode]
  );

  const handleNodeConfigClose = () => {
    if (forcedSidebarNode) {
      const nodeType = getNodeConfig(forcedSidebarNode.type || '');
      if (nodeType?.onAsyncSave) {
        return;
      }
      handleNodeDelete([forcedSidebarNode]);
    }

    setSelectedNodeId(null);
    setForcedSidebarNode(null);
  };

  // Local handlers for draft state
  const onNodesChange: OnNodesChange = useCallback(changes => {
    setDraftNodes(nodes => applyNodeChanges(changes, nodes));
  }, []);

  const onEdgesChange: OnEdgesChange = useCallback(changes => {
    setDraftEdges(edges => applyEdgeChanges(changes, edges));
  }, []);

  const {
    onConnect,
    isValidConnection,
    onNodesDelete,
    onEdgesDelete,
    onNodeUpdate,
    handleDragOver,
    handleDrop,
    nodeTypes,
    reactFlowWrapper,
    pendingDrop,
    isCopying,
    onConfirmDrop,
    onCancelDrop,
  } = useWorkflowCanvas({
    nodes: draftNodes,
    edges: draftEdges,
    setNodes: setDraftNodes,
    setEdges: setDraftEdges,
    onNodeDropped: handleNodeDropped,
    onAsyncNodeDropped: handleAsyncNodeDropped,
  });

  const { validateWorkflow } = useNodeValidation();

  const validationState = useMemo(() => {
    const validationResult = validateWorkflow(draftNodes, draftEdges);

    // Derive nodesWithErrors from validation result
    const nodesWithErrors = [...new Set(validationResult.nodeErrors.map(e => e.nodeId))];

    // Total error count (workflow + node errors)
    const totalErrorCount =
      validationResult.workflowErrors.length + validationResult.nodeErrors.length;

    return {
      isValid: validationResult.isValid,
      workflowErrors: validationResult.workflowErrors,
      nodeErrors: validationResult.nodeErrors,
      nodesWithErrors,
      totalErrorCount,
    };
  }, [draftNodes, draftEdges, validateWorkflow]);

  // Create WorkflowError context value for sharing error state with node renderers
  const workflowErrorContextValue: WorkflowErrorContextValue = useMemo(
    () => ({
      showErrors,
      setShowErrors,
      nodeErrors: validationState.nodeErrors,
      workflowErrors: validationState.workflowErrors,
      getNodeErrors: (nodeId: string) =>
        validationState.nodeErrors.filter(e => e.nodeId === nodeId),
    }),
    [showErrors, validationState.nodeErrors, validationState.workflowErrors]
  );

  // Handle save with draft data - validate first, show errors if invalid
  const handleSave = useCallback(() => {
    if (!validationState.isValid) {
      setShowErrors(true);
      toaster.create('Workflow agent update failed', {
        intent: 'danger',
      });
      return;
    }
    onSave(draftNodes, draftEdges);
  }, [onSave, draftNodes, draftEdges, validationState.isValid]);

  const handleNodeUpdate = (nodeId: string, data: Record<string, unknown>) => {
    onNodeUpdate(nodeId, data);

    if (forcedSidebarNode && forcedSidebarNode.id === nodeId) {
      const nodeType = getNodeConfig(forcedSidebarNode.type || '');
      if (nodeType?.onAsyncSave && data) {
        setForcedSidebarNode(null);
        setHasUnsavedAsyncNode(false);
      }
    }
  };

  const handleNodeDelete = (nodes: Node[]) => {
    onNodesDelete(nodes);

    // Close sidebar if the deleted node is currently selected
    if (selectedNodeId && nodes.some(node => node.id === selectedNodeId)) {
      setSelectedNodeId(null);
    }

    if (forcedSidebarNode && nodes.some(node => node.id === forcedSidebarNode.id)) {
      setForcedSidebarNode(null);
      setHasUnsavedAsyncNode(false);
    }
  };

  // Inject isUnsavedAsync flag into node data for rendering (showErrors is now in context)
  const nodesWithFlags = useMemo(() => {
    return draftNodes.map(node => {
      const isUnsavedAsync = forcedSidebarNode?.id === node.id;
      if (!isUnsavedAsync) return node;
      return {
        ...node,
        data: {
          ...node.data,
          isUnsavedAsync: true,
        },
      };
    });
  }, [draftNodes, forcedSidebarNode]);

  return (
    <WorkflowErrorContext.Provider value={workflowErrorContextValue}>
      <Flex className={styles.layoutContainer}>
        <WorkflowBuilderTopbar
          onMenuClick={handleMenuClick}
          onSave={handleSave}
          onClose={handleClose}
          isMenuOpen={isSidebarOpen}
          title={agentFormData?.name || 'Workflow Agent Name'}
          isSaveDisabled={hasUnsavedAsyncNode}
          saveDisabledReason={
            hasUnsavedAsyncNode
              ? 'Please save or delete the unsaved node before saving the workflow'
              : undefined
          }
          showErrors={showErrors}
          setShowErrors={setShowErrors}
          errorCount={validationState.totalErrorCount}
        />
        <div className={styles.sidebarArea}>
          <Sidebar open={isSidebarOpen && !isSACreationOpen} initialWidth={350} dock="left" top={0}>
            <WorkFlowLeftSidebar
              hasUnsavedAsyncNode={hasUnsavedAsyncNode}
              onOpenSACreation={handleOpenSACreation}
            />
          </Sidebar>
          <Sidebar open={isSACreationOpen} initialWidth={500} dock="left" top={0}>
            {isSACreationOpen && (
              <SpecializedAgentCreationForm isEmbedded onClose={handleCloseSACreation} />
            )}
          </Sidebar>
          <div className={styles.mainContent}>
            <WorkflowCanvas
              nodes={nodesWithFlags}
              edges={draftEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={handleNodeClick}
              onConnect={onConnect}
              isValidConnection={isValidConnection}
              onNodesDelete={handleNodeDelete}
              onEdgesDelete={onEdgesDelete}
              handleDragOver={handleDragOver}
              handleDrop={handleDrop}
              nodeTypes={nodeTypes}
              reactFlowWrapper={reactFlowWrapper}
              showErrors={showErrors}
              workflowErrors={validationState.workflowErrors}
            />
          </div>
          <Sidebar open={!!selectedNode} dock="right" top={0} initialWidth={420}>
            <WorkflowRightSidebar
              key={selectedNodeId}
              selectedNode={selectedNode}
              onClose={handleNodeConfigClose}
              onNodeUpdate={handleNodeUpdate}
              onNodeDelete={handleNodeDelete}
              isForced={!!forcedSidebarNode && forcedSidebarNode.id === selectedNode?.id}
            />
          </Sidebar>
        </div>

        <ConfirmationModal
          isShown={showUnsavedChangesModal}
          title="Unsaved Changes"
          onCancel={handleCancelClose}
          onConfirm={handleConfirmClose}
          cancelLabel="Stay"
          action="Discard Changes"
          appearance="danger"
        >
          You have unsaved changes in the workflow. If you close now, all changes will be lost.
        </ConfirmationModal>

        <ConfirmationModal
          isShown={pendingDrop !== null}
          title="Copy Agent"
          onCancel={onCancelDrop}
          onConfirm={onConfirmDrop}
          action="Copy Agent"
          appearance="primary"
          confirmLoading={isCopying}
          cancelDisabled={isCopying}
          size="md"
        >
          <Text>
            Optimizely managed agents must be copied in order to use in a workflow.{' '}
            <Link href="https://support.optimizely.com/hc/en-us/articles/41942756023309">
              Learn more.
            </Link>
          </Text>
          <Text mt="8">Would you like to create a copy?</Text>
        </ConfirmationModal>
      </Flex>
    </WorkflowErrorContext.Provider>
  );
}
